/**
 * The one confirm in the product.
 *
 * Seven hand-rolled dialogs used to do this job — `Modal`'s four variants, `SettingsDialog`,
 * `BillingDialog`, `CelebrationModal`, and three private overlays — at eight widths, six radii and
 * five different reds, two of which were fallbacks for tokens that were never defined. This is
 * deck15's `ConfirmDialog` promoted out of the directory, with the two things it was missing:
 *
 *   tone      danger | primary. The old one hard-coded the red, which is why "Restyle", "Rewrite"
 *             and "Replace" — all recoverable, two of them additive — arrived dressed as deletes.
 *   variants  an input (retires the last window.prompt), a word to type, a third answer, and a
 *             hero band for the billing receipts.
 *
 * Geometry, type ramp and pills are unchanged from the original. Focus is trapped, which nothing
 * in the product did before.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '~/lib/utils';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import type { HeaderTheme } from '~/design-system/components/v1_5/Directory';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';
import styles from './ConfirmDialog.module.css';

export type ConfirmTone = 'danger' | 'primary';

export interface ConfirmInputSpec {
  label?: string;
  placeholder?: string;
  initial?: string;
  /** Monospace the field — used for the type-to-confirm word, not for prose. */
  mono?: boolean;
}

export interface ConfirmSecondaryAction {
  label: string;
  onSelect: () => void;
}

export interface ConfirmDialogProps {
  title: string;
  body?: ReactNode;
  /** Defaults to `danger`. Use `primary` for anything recoverable — see the module note. */
  tone?: ConfirmTone;
  confirmLabel?: string;
  /** Omit to hide the cancel button entirely: that is the `alert` shape. */
  cancelLabel?: string | null;
  /**
   * Paints cancel's label red. Only for the three-way, where the quiet answer is the LOSSY one
   * ("Discard", "Leave") rather than the safe one.
   */
  cancelTone?: 'quiet' | 'danger';
  /** A third button between cancel and confirm. Present ⇒ the panel widens to 420. */
  secondary?: ConfirmSecondaryAction;
  /** Full-bleed band above the copy. The billing receipts' big credit number lives here. */
  hero?: ReactNode;
  /** Renders a text field; `onConfirm` receives its trimmed value. */
  input?: ConfirmInputSpec;
  /** Confirm stays disabled until the field matches this exactly. Widens the panel to 420. */
  requireWord?: string;
  /** Confirm shows as busy and the dialog stops accepting dismissals. */
  busy?: boolean;
  /**
   * Which button takes focus on open. Defaults to the SAFE one — the old dialog focused the
   * destructive button on purpose (ConfirmDialog.tsx:34) so Enter completed the delete, but this
   * component now also guards account deletion and plan cancellation. Pass 'confirm' to restore
   * the old behaviour at a single call site.
   */
  initialFocus?: 'safe' | 'confirm';
  onConfirm: (value: string) => void;
  onCancel: () => void;
  /**
   * Escape and a backdrop press, when they should NOT mean the same thing as the cancel button.
   *
   * In a two-answer dialog they do, and this can be omitted. In the three-way they must not: there
   * the quiet button is "Discard", and a reader who hits Escape is saying "never mind, put me
   * back" — not "throw my work away". Without this, Escape was the most destructive key on that
   * surface. Falls back to `onCancel` when absent.
   */
  onDismiss?: () => void;
  theme?: HeaderTheme;
  /**
   * Render the panel on its own — no portal, no backdrop, no focus stealing, no Escape handler.
   *
   * Exists so the Storybook gallery and any visual-regression shot can lay every dialog in the
   * product out side by side. A modal cannot do that: twenty-five fixed, full-screen backdrops
   * stack into one. The alternative was re-typing the markup in the story, which would drift from
   * the component the day someone changed it. Not for product use.
   */
  embedded?: boolean;
}

const FOCUSABLE = 'button:not(:disabled), input, [href], [tabindex]:not([tabindex="-1"])';

export function ConfirmDialog({
  title,
  body,
  tone = 'danger',
  confirmLabel,
  cancelLabel = 'Cancel',
  cancelTone = 'quiet',
  secondary,
  hero,
  input,
  requireWord,
  busy = false,
  initialFocus = 'safe',
  onConfirm,
  onCancel,
  onDismiss,
  theme: themeProp,
  embedded = false,
}: ConfirmDialogProps) {
  const theme = useV15Theme(themeProp);
  const panelRef = useRef<HTMLDivElement>(null);
  const safeRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const titleId = useId();
  const bodyId = useId();

  // `requireWord` implies a field. Without this, a call site that passed the word but forgot the
  // input would render a confirm button that can never arm — a dead dialog with no way forward.
  // Memoised because it is a focus-effect dependency: a fresh object each render would re-run the
  // effect on every keystroke and yank the caret back to the start of the field.
  const field: ConfirmInputSpec | undefined = useMemo(
    () => input ?? (requireWord !== undefined ? { placeholder: requireWord, mono: true } : undefined),
    [input, requireWord],
  );

  const [value, setValue] = useState(field?.initial ?? '');
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const armed = requireWord === undefined || value.trim() === requireWord;
  const canConfirm = armed && !busy;

  // What Escape and the backdrop mean. The cancel BUTTON always calls `onCancel` directly.
  const dismiss = useCallback(() => {
    if (busy) return;
    (onDismiss ?? onCancel)();
  }, [busy, onCancel, onDismiss]);

  const commit = useCallback(() => {
    if (!canConfirm) return;
    onConfirm(value.trim());
  }, [canConfirm, onConfirm, value]);

  // A field wants the caret; otherwise the safe answer, unless the call site asks for the other.
  //
  // Once per open, guarded by a ref rather than by dependencies: call sites pass `input` as an
  // inline object literal, so any dep on it changes identity every render and would drag focus
  // back to the start of the field on each keystroke. ConfirmHost keys the dialog per question,
  // so a new question remounts and focuses again.
  const focused = useRef(false);
  useEffect(() => {
    if (embedded || focused.current) return;
    focused.current = true;
    if (field) inputRef.current?.focus();
    else if (initialFocus === 'confirm') confirmRef.current?.focus();
    else (safeRef.current ?? confirmRef.current)?.focus();
  }, [embedded, field, initialFocus]);

  // Escape cancels, and Tab cycles inside the panel. The trap is the one thing no dialog in this
  // product had — without it Tab walks straight out into the page behind the scrim.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        dismiss();
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const stops = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (stops.length === 0) return;
      const first = stops[0];
      const last = stops[stops.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !panel.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    if (embedded) return;
    // Capture, with propagation stopped above, so a page-level Escape behind the scrim never sees
    // it. brand-kits is the case that proves the need: its page Escape means "leave the kit"
    // (brand-kits_.$kitId.tsx), and a dialog that let that through would answer the question and
    // navigate away in one keystroke. Doing it here means no call site repeats it — note the
    // hand-rolled capture handlers still in that file guard OTHER surfaces (the fullscreen
    // reference viewer, the card menu, the Figma form), not confirms, so they stay.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [dismiss, embedded]);

  const wide = Boolean(secondary) || requireWord !== undefined;
  const resolvedConfirmLabel = confirmLabel ?? (tone === 'danger' ? 'Delete' : 'Continue');

  const panel = (
      <div
        ref={panelRef}
        className={cn(styles.panel, wide && styles.panel_wide)}
        role="alertdialog"
        aria-modal
        aria-labelledby={titleId}
        aria-describedby={body ? bodyId : undefined}
      >
        {hero ? <div className={styles.hero}>{hero}</div> : null}
        <div className={styles.body_pad}>
          <div className={styles.title} id={titleId}>
            {title}
          </div>
          {body ? (
            <div className={styles.body} id={bodyId}>
              {body}
            </div>
          ) : null}

          {field ? (
            <>
              {field.label ? <div className={styles.field_label}>{field.label}</div> : null}
              <input
                ref={inputRef}
                className={cn(styles.field, field.mono && styles.field_mono)}
                value={value}
                placeholder={field.placeholder}
                aria-label={field.label ?? title}
                onChange={(event) => setValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    commit();
                  }
                }}
              />
            </>
          ) : null}

          <div className={styles.actions}>
            {cancelLabel === null ? null : (
              <button
                type="button"
                ref={safeRef}
                className={cn(
                  styles.button,
                  cancelTone === 'danger' ? styles.quiet_danger : styles.quiet,
                )}
                onClick={() => {
                  if (!busy) onCancel();
                }}
              >
                {cancelLabel}
              </button>
            )}
            {secondary ? (
              <button
                type="button"
                className={cn(styles.button, styles.quiet)}
                onClick={secondary.onSelect}
              >
                {secondary.label}
              </button>
            ) : null}
            <button
              type="button"
              ref={confirmRef}
              disabled={!canConfirm}
              className={cn(styles.button, tone === 'danger' ? styles.danger : styles.primary)}
              onClick={commit}
            >
              {resolvedConfirmLabel}
            </button>
          </div>
        </div>
      </div>
  );

  if (embedded) return <div className={themes[theme]}>{panel}</div>;

  const dialog = (
    // Backdrop press-to-cancel is a pointer convenience over the real keyboard path (Escape, plus
    // the trapped tab ring) — a key handler HERE would double-handle.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={cn(themes[theme], styles.backdrop)}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) dismiss();
      }}
    >
      {panel}
    </div>
  );

  // Portalled so the dialog stops inheriting a host's stacking context. The old one was
  // position:fixed in-tree, which is why SettingsDialog's scrim stopped at the settings rail.
  if (!mounted) return null;
  return createPortal(dialog, document.body);
}
